import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PurchaseButton from './PurchaseButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndustry, faBolt, faBoxOpen, faTag, faPlug, faLayerGroup,
  faStore, faCircleCheck, faTruck, faBatteryFull, faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { formatKwota, formatPricePerWp } from '../utils';
import AvailabilityBadge from './AvailabilityBadge';
import AvailabilityTrend from './AvailabilityTrend';
import ProductActions from './ProductActions';
import { ProductImage } from './ProductImage';
import PriceHistoryChart from './PriceHistoryChart';
import { useSelection, selectionKey } from '../context/SelectionContext';
import { etykietaMocySkrocona } from '../utils/etykietaMocy';

// Ścieżka karty produktu bywa inna niż productType — dla wallboxów adres jest
// po polsku (/ladowarka/), tak jak pozostałe adresy w serwisie.
const DETAIL_PATH = { wallbox: 'ladowarka' };

// Skróty na kafelku listy; pełne etykiety są na karcie produktu.
// Teksty w słowniku `model`: oferta.zlacze_skrot.<złącze>.
const ZLACZA_ZE_SKROTEM = ['gniazdo_Type2', 'kabel_Type2', 'kabel_Type1'];

const Attr = ({ icon, label, children, compact }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5 text-gray-400">
      <FontAwesomeIcon icon={icon} className="text-sm" />
      <span className="text-xs uppercase tracking-wide">{label}</span>
    </div>
    <div className={`mt-1 ${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-800 truncate`}>{children}</div>
  </div>
);

const emptyDash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

const SellerValue = ({ seller }) => (
  <span className="inline-flex items-center gap-1 min-w-0">
    <span className="truncate">{emptyDash(seller)}</span>
    {seller && <FontAwesomeIcon icon={faCircleCheck} className="text-blue-500 text-sm flex-shrink-0" />}
  </span>
);

const Product = ({ product, productType, recommended = false, compact = false }) => {
  const { t } = useTranslation('model');
  // Faza, napięcie i rodzaj przychodzą z API jako wartości katalogu („1 Fazowy”,
  // „Hybrydowy”). Wartość zostaje bez zmian, tłumaczymy tylko to, co widać.
  const wartosc = (grupa, v) => (v ? t(`wartosc.${grupa}.${v}`, { defaultValue: v }) : v);
  const { openHistoryChart, closeHistoryChart, isHistoryChartOpen } = useSelection();
  // Historia ceny jest liczona per-model — dostępna tylko gdy oferta ma znany model_id
  // (np. z widoku szczegółów produktu). Bez tego przycisk jest po prostu ukryty.
  const hasHistory = product.model_id != null;
  const historyKey = hasHistory ? selectionKey(productType, product.model_id) : null;
  const historyOpen = hasHistory && isHistoryChartOpen(historyKey);

  const isMagazyn = productType === 'magazyn';
  const powerValue =
    productType === 'panel' ? (product.power != null ? `${product.power} Wp` : '—')
    : productType === 'inwerter' ? (product.power != null ? `${product.power} kW` : '—')
    // Na kafelku listy moc wiodąca z kwalifikatorem; obie wartości są na karcie.
    : productType === 'wallbox' ? etykietaMocySkrocona(product)
    : isMagazyn ? (product.capacity != null ? `${product.capacity} kWh` : '—')
    : '—';
  const sizeIcon = isMagazyn ? faBatteryFull : faBolt;
  // Przy wallboxach kwalifikator prądu jest w samej wartości (np. „22 kW AC"),
  // bo na kafelku listy nie ma miejsca na dłuższą etykietę.
  const sizeLabel = isMagazyn ? t('pole.pojemnosc') : t('pole.moc');

  const grossPrice = formatKwota(product.price, 0);
  const nettoPrice = formatKwota(product.price_netto, 2);
  const pricePerWp = formatPricePerWp(product);

  const thirdAttr =
    productType === 'panel' ? <Attr icon={faTag} label={t('pole.cena_za_wp')} compact={compact}>{emptyDash(pricePerWp)}</Attr>
    : productType === 'inwerter' ? <Attr icon={faPlug} label={t('pole.fazy')} compact={compact}>{emptyDash(wartosc('faza', product.phase))}</Attr>
    : productType === 'magazyn' ? <Attr icon={faBolt} label={t('pole.napiecie')} compact={compact}>{emptyDash(wartosc('napiecie', product.voltage))}</Attr>
    : productType === 'wallbox' ? <Attr icon={faPlug} label={t('pole.zlacze')} compact={compact}>{emptyDash(ZLACZA_ZE_SKROTEM.includes(product.connector_type) ? t(`oferta.zlacze_skrot.${product.connector_type}`) : null)}</Attr>
    : null;

  const availAttr = (
    <Attr icon={faBoxOpen} label={t('pole.dostepnosc')} compact={compact}>
      <span className="inline-flex items-center gap-1">
        <AvailabilityBadge value={product.availability} />
        <AvailabilityTrend
          availability={product.availability}
          availability_prev={product.availability_prev}
          availability_prev_ts={product.availability_prev_ts}
          availability_prev2={product.availability_prev2}
          availability_prev2_ts={product.availability_prev2_ts}
        />
      </span>
    </Attr>
  );

  const imgCls = compact ? 'w-full md:w-36 h-36' : 'w-full md:w-48 h-48';
  const titleCls = compact ? 'text-base' : 'text-lg sm:text-xl';
  const gridCls = compact ? 'mt-3 gap-x-4 gap-y-3' : 'mt-4 gap-x-6 gap-y-5';
  const priceCls = compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl';
  const rightCol = compact ? 'md:w-[172px]' : 'md:w-[190px]';

  return (
    <li className={`relative flex flex-col md:flex-row gap-4 ${compact ? 'p-3 mb-4' : 'p-4 mb-6'} bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow`}>
      {recommended && (<span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-md bg-blue-600 text-white text-[11px] font-semibold px-2 py-0.5 shadow-sm">{t('oferta.poleca')}</span>)}
      <Link to={`/${DETAIL_PATH[productType] || productType}/${product.id}`} className="flex flex-col md:flex-row gap-4 flex-grow min-w-0" style={{ textDecoration: 'none', color: 'inherit' }}>
        <ProductImage src={product.img_url} productType={productType} alt={product.name} className={`${imgCls} object-contain rounded-xl border border-gray-100 bg-white p-2 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold ${titleCls} text-gray-900 line-clamp-2`}>{product.name}</h3>
          <div className={`grid grid-cols-2 sm:grid-cols-3 ${gridCls}`}>
            <Attr icon={faIndustry} label={t('pole.producent')} compact={compact}>{emptyDash(product.manufacturer)}</Attr>
            <Attr icon={sizeIcon} label={sizeLabel} compact={compact}>{powerValue}</Attr>
            {compact
              ? <Attr icon={faLayerGroup} label={t('pole.typ')} compact={compact}>{emptyDash(wartosc('typ', product.type))}</Attr>
              : availAttr}

            {compact ? (
              <>
                <Attr icon={faStore} label={t('pole.sprzedawca')} compact={compact}><SellerValue seller={product.seller} /></Attr>
                <Attr icon={faTruck} label={t('pole.wysylka')} compact={compact}>{emptyDash(product.shipping)}</Attr>
                {availAttr}
              </>
            ) : (
              <>
                <Attr icon={faLayerGroup} label={t('pole.rodzaj')} compact={compact}>{emptyDash(wartosc('typ', product.type))}</Attr>
                <Attr icon={faStore} label={t('pole.sprzedawca')} compact={compact}><SellerValue seller={product.seller} /></Attr>
                {thirdAttr}
              </>
            )}
          </div>
        </div>
      </Link>

      <div className={`flex flex-row md:flex-col items-center md:items-center justify-between md:justify-center gap-3 md:pl-5 md:border-l md:border-gray-100 flex-shrink-0 ${rightCol}`} onClick={(e) => e.stopPropagation()}>
        <ProductActions product={product} productType={productType} size={compact ? 'sm' : 'md'} />
        <div className="text-left md:text-center">
          {product.price_on_request && grossPrice === null ? (
            <p className="font-semibold text-gray-600 text-sm md:text-base whitespace-normal break-words max-w-[140px] md:max-w-full mx-auto">{t('oferta.cena_dla_zalogowanych')}</p>
          ) : (
            <>
              <div className="flex items-center gap-2 md:justify-center">
                <p className={`font-extrabold text-blue-600 ${priceCls} whitespace-nowrap`}>{grossPrice !== null ? grossPrice : t('oferta.brak_ceny')}</p>
                {hasHistory && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openHistoryChart(historyKey); }}
                    aria-label={t('pokaz_historie_ceny')}
                    title={t('historia_ceny')}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors shrink-0 text-xs"
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                  </button>
                )}
              </div>
              {nettoPrice !== null && (<p className="text-xs text-gray-400 whitespace-nowrap">{t('oferta.bez_vat', { kwota: nettoPrice })}</p>)}
              {compact && productType === 'panel' && pricePerWp && (<p className="text-xs font-semibold text-gray-500 whitespace-nowrap">{pricePerWp}</p>)}
            </>
          )}
        </div>
        <PurchaseButton link={product.link} size={compact ? 'sm' : 'md'} />
      </div>
      {hasHistory && (
        <PriceHistoryChart
          modelId={product.model_id}
          category={productType}
          modelName={product.name}
          open={historyOpen}
          onClose={() => closeHistoryChart(historyKey)}
        />
      )}
    </li>
  );
};

export default Product;
